// QR Kiosk v2 — KV2-9 Tier 0 nameplate API. Binds the ANONYMOUS B7-BE2 endpoint on
// machineryApiBaseUrl:
//   GET /v1/nameplate/{code}   → NameplateViewModel | 404 | 429
//
// NO public/anonymous document exposure — owner ruling 2026-08-13 (D14): documents are
// login-only. Do not re-add a visibility tick or a public docs list without a new owner
// ruling. The second binding here (…/documents/{id}/download) and its URL builder are gone;
// the route itself is deleted server-side in the same release, so any re-added call 404s.
//
// Deliberately NOT core/api — the same two reasons as guest/guestAccessApi.ts: the shared
// axios interceptor (a) masks the HTTP status into { message, code } (we need 429 apart from
// other 4xx to choose a message) and (b) on a 401 calls accountContext.logout() + navigates
// to "/", which is catastrophic on a page whose whole point is that the visitor is anonymous.
// This bare instance sends no Authorization header and preserves err.response.status.
import Axios from "axios";
import { Nameplate } from "./nameplateModels";

const nameplateApi = Axios.create({ baseURL: (window as any).app.config.machineryApiBaseUrl });

// The server returns a UNIFORM, byte-identical 404 for unknown / revoked / disabled /
// decommissioned / deleted / malformed codes (SEC-REQ-B7-7 — no state oracle). 404 and 400
// therefore COLLAPSE to one kind on purpose: the UI must not be able to distinguish them,
// because distinguishing them is exactly the enumeration oracle the design forbids.
export type NameplateErrorKind = "notFound" | "rateLimited" | "network";

// A plain tagged failure (NOT a subclassed Error): the app compiles to es5, where
// `class X extends Error` loses the prototype chain and `instanceof` silently fails —
// that would collapse every failure to "network" and lose the 429 signal.
export interface NameplateFailure {
  isNameplateFailure: true;
  kind: NameplateErrorKind;
}

function fail(err: any): NameplateFailure {
  const status = err?.response?.status;
  const kind: NameplateErrorKind =
    status === 429 ? "rateLimited" : typeof status === "number" && status >= 400 && status < 500 ? "notFound" : "network";
  return { isNameplateFailure: true, kind };
}

/** Read the kind off a thrown nameplate failure (es5-safe — no instanceof). */
export function nameplateErrorKind(err: any): NameplateErrorKind {
  const k = err?.kind;
  return k === "notFound" || k === "rateLimited" || k === "network" ? k : "network";
}

export async function getNameplate(code: string): Promise<Nameplate> {
  try {
    const { data } = await nameplateApi.get<Nameplate>(`/v1/nameplate/${encodeURIComponent(code)}`);
    return data;
  } catch (err) {
    throw fail(err);
  }
}
